import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { buildAbilityFromJwt, type Action, type AppAbility } from '@/shared/permissions/ability';
import { useAuth } from './AuthProvider';

const AbilityContext = createContext<AppAbility>(buildAbilityFromJwt(null));

export function AbilityProvider({ children }: { children: ReactNode }) {
  const { decoded } = useAuth();
  const ability = useMemo(() => buildAbilityFromJwt(decoded), [decoded]);
  return <AbilityContext.Provider value={ability}>{children}</AbilityContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAbility(): AppAbility {
  return useContext(AbilityContext);
}

interface CanProps {
  /** Action (alias of `do`). */
  I?: Action;
  /** Action (alias of `I`). */
  do?: Action;
  /** Subject type. */
  a: string;
  /** Negate: render only when NOT allowed. */
  not?: boolean;
  /** When passThrough is set, always renders children; useful when children are functions. */
  passThrough?: boolean;
  children: ReactNode | ((allowed: boolean) => ReactNode);
}

/**
 * Render-prop gate based on the user's ability.
 * Usage: <Can I="create" a="Project">{children}</Can>
 */
export function Can({ I, do: act, a, not, passThrough, children }: CanProps) {
  const ability = useAbility();
  const action = (I ?? act ?? 'read') as Action;
  const allowed = ability.can(action, a);
  const effective = not ? !allowed : allowed;
  if (typeof children === 'function') return <>{(children as (allowed: boolean) => ReactNode)(effective)}</>;
  if (passThrough) return <>{children}</>;
  return effective ? <>{children}</> : null;
}
